import React, { useEffect, useState, useContext } from 'react';
import { Modal, Box, Button, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { getMaintenanceBannerInfo } from '../../api/settings';
import { getMaintenance } from '../../api/maintenance';
import { StoreContext } from '../Store';
import './MaintenanceBanner.css';

const htmlSanitizeConfig = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'u', 'strong', 'a', 'p', 'br'],
  ALLOWED_ATTR: ['href', 'target'],
};

const normalizeNewApi = (data) => {
  if (!data) return null;
  return {
    enabled: data.enabled === true || parseInt(data.enabled, 10) === 1,
    bannerText: data.bannerText || '',
    moreInfo: data.moreInfo || '',
  };
};

const normalizeLegacy = (data) => {
  if (!data) return null;
  return {
    enabled: parseInt(data.flag, 10) === 1,
    bannerText: data.message || '',
    moreInfo: '',
  };
};

const MaintenanceBanner = ({ previewData }) => {
  const store = useContext(StoreContext);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [banner, setBanner] = useState({});

  const fetchData = async () => {
    if (previewData) return;
    try {
      const data = await getMaintenanceBannerInfo();
      const n = normalizeNewApi(data);
      if (n && (n.bannerText !== undefined || n.enabled !== undefined)) {
        setBanner(n);
        return;
      }
    } catch {
      // maintenance-banner/info not available — fall back to legacy maintenance endpoint
    }
    try {
      const legacy = await getMaintenance();
      const n = normalizeLegacy(legacy);
      if (n) setBanner(n);
    } catch {
      setBanner({});
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (store.maintenanceRefresh?.[0]) {
      fetchData();
      store.maintenanceRefresh[1](false);
    }
  }, [store.maintenanceRefresh?.[0]]);

  useEffect(() => {
    if (previewData) {
      setBanner({ ...previewData });
    }
  }, [previewData]);

  const onHide = () => setShowMoreInfo(false);

  if (!banner.enabled || !banner.bannerText) {
    return null;
  }

  return (
    <>
      <div className="maintenance-banner">
        <p>
          {banner.bannerText}
          {banner.moreInfo !== '' && banner.moreInfo != null && (
            <button type="button" className="more-info-button" onClick={() => setShowMoreInfo(true)}>
              [More info]
            </button>
          )}
        </p>
      </div>

      <Modal open={showMoreInfo} onClose={onHide} className="maintenance-banner-modal">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: 800,
            bgcolor: 'background.paper',
            border: 'none',
            outline: 'none',
            p: 4,
            borderRadius: 5,
          }}
        >
          <Box sx={{ py: 3, px: 3 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
              {banner?.bannerText}
            </Typography>
          </Box>
          <Box
            sx={{ py: 3, px: 3 }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(banner?.moreInfo || '', htmlSanitizeConfig),
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 3, px: 3 }}>
            <Button variant="contained" onClick={() => setShowMoreInfo(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default MaintenanceBanner;
